import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { gql, useMutation } from '@apollo/client';

const CREATE_EXIT_PLAN_SPRINT = gql`
  mutation CreateExitPlanSprint($confirm: Boolean, $reuse: Boolean) {
    createExitPlanSprint(confirm_close_active: $confirm, reuse_existing: $reuse) {
      ok
    }
  }
`;

const ExitPlanOverlay = ({ isOpen, onClose }) => {
  const [step, setStep] = useState('processing'); // 'processing' | 'prompt_close_active' | 'prompt_reuse_existing' | 'done'
  const [createSprint, { loading }] = useMutation(CREATE_EXIT_PLAN_SPRINT);
  const startedAtRef = useRef(null);
  const minShowMs = 600; // always show processing at least briefly

  const startCreate = async (opts = { confirm: false, reuse: false }) => {
    try {
      try { console.debug('[ExitPlanOverlay] invoking createExitPlanSprint', opts); } catch (_) {}
      const { data } = await createSprint({ variables: { confirm: !!opts.confirm, reuse: !!opts.reuse } });
      let res = data?.createExitPlanSprint || {};
      try { console.debug('[ExitPlanOverlay] result', res); } catch (_) {}
      if (res.ok) {
        const elapsed = Date.now() - (startedAtRef.current || Date.now());
        const wait = Math.max(0, 3000 - elapsed);
        setStep('processing');
        setTimeout(() => setStep('done'), wait);
      } else {
        // If server didn't expose flags, attempt a forced proceed path
        try {
          const r2 = await createSprint({ variables: { confirm: true, reuse: true } });
          res = r2?.data?.createExitPlanSprint || {};
          try { console.debug('[ExitPlanOverlay] forced proceed result', res); } catch (_) {}
        } catch (e2) {
          try { console.error('[ExitPlanOverlay] forced proceed error', e2); } catch (_) {}
        }
        const elapsed = Date.now() - (startedAtRef.current || Date.now());
        const wait = Math.max(minShowMs, 3000 - elapsed);
        setStep('processing');
        setTimeout(() => setStep('done'), wait);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      try {
        const details = { message: e?.message, graphQLErrors: e?.graphQLErrors, networkError: e?.networkError };
        console.error('[ExitPlanOverlay] create error', details);
      } catch (_) {}
      const elapsed = Date.now() - (startedAtRef.current || Date.now());
      const wait = Math.max(minShowMs, 3000 - elapsed);
      setStep('processing');
      setTimeout(() => setStep('done'), wait);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setStep('processing');
      startedAtRef.current = Date.now();
      // mimic WizardOverlay behavior: add global class to hide underlying UI and lock scroll
      try {
        document.documentElement.classList.add('intake-overlay-active');
        document.documentElement.style.overflow = 'hidden';
        if (window.ahoy?.track) window.ahoy.track('exit_plan_overlay_opened');
      } catch (_) {}
      startCreate({ confirm: false, reuse: false });
    }
    return () => {
      try {
        document.documentElement.classList.remove('intake-overlay-active');
        document.documentElement.style.overflow = '';
      } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Render via portal at document.body to escape any transformed/overflow parents
  const portalElRef = useRef(null);
  if (!portalElRef.current && typeof document !== 'undefined') {
    portalElRef.current = document.createElement('div');
    portalElRef.current.setAttribute('data-exit-plan-overlay', 'true');
  }
  useEffect(() => {
    if (!portalElRef.current) return () => {};
    try { document.body.appendChild(portalElRef.current); } catch (_) {}
    return () => {
      try { if (portalElRef.current?.parentNode) portalElRef.current.parentNode.removeChild(portalElRef.current); } catch (_) {}
    };
  }, []);

  if (!isOpen) return null;

  const Processing = () => (
    <div className="flex items-center justify-center min-h-screen p-8">
      <div className="text-center space-y-8 max-w-2xl">
        <style>{`@keyframes fb-spin{to{transform:rotate(360deg)}}`}</style>
        <div className="w-24 h-24 bg-succession-primary rounded-full flex items-center justify-center mx-auto">
          <div className="w-12 h-12" style={{ animation: 'fb-spin 1s linear infinite' }} aria-hidden="true">
            <svg className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-30" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-90" fill="currentColor" d="M12 2a10 10 0 0 1 10 10h-4a6 6 0 0 0-6-6V2z" />
            </svg>
          </div>
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-succession-foreground">Creating Your Exit Plan</h1>
        <p className="text-lg text-succession-text-primary">We’re preparing your 90-day Exit Plan Sprint with key tasks.</p>
      </div>
    </div>
  );

  const Done = () => (
    <div className="flex items-center justify-center min-h-screen p-8">
      <div className="text-center space-y-8 max-w-2xl">
        <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-succession-foreground">Exit Sprint Plan Complete!</h1>
        <p className="text-lg text-succession-text-primary">Your Exit Plan Sprint is ready with important exit planning tasks.</p>
        <button type="button" className="px-8 py-4 bg-succession-primary text-white rounded-xl font-semibold hover:bg-succession-primary/90" onClick={() => { try { const url = new URL('/succession/sprint', window.location.origin); url.searchParams.set('sprintId', 'active'); url.searchParams.set('exitPlanTour', '1'); window.location.href = url.toString(); } catch (_) {} onClose?.(); }}>View My Sprint</button>
      </div>
    </div>
  );

  const Prompt = ({ title, body, onProceed }) => (
    <div className="flex items-center justify-center min-h-screen p-8">
      <div className="text-center space-y-6 max-w-2xl">
        <h1 className="text-3xl md:text-4xl font-bold text-succession-foreground">{title}</h1>
        <p className="text-lg text-succession-text-primary">{body}</p>
        <div className="flex gap-3 justify-center">
          <button type="button" className="px-6 py-3 rounded-xl border border-succession-border text-succession-foreground" onClick={() => onClose?.()}>Cancel</button>
          <button type="button" className="px-6 py-3 rounded-xl bg-succession-primary text-white" disabled={loading} onClick={onProceed}>{loading ? 'Working…' : 'Proceed'}</button>
        </div>
      </div>
    </div>
  );

  const overlayNode = (
    <div className="fixed inset-0 z-50 bg-succession-bg-primary text-succession-foreground" role="dialog" aria-modal="true">
      <style>
        {`
        /* Theme-aware text colors for overlay content */
        html.intake-overlay-active body,
        html.intake-overlay-active body *,
        html.intake-overlay-active body.wizard-body,
        html.intake-overlay-active body.wizard-body *,
        html.intake-overlay-active h1,
        html.intake-overlay-active h2,
        html.intake-overlay-active h3,
        html.intake-overlay-active h4,
        html.intake-overlay-active h5,
        html.intake-overlay-active h6,
        html.intake-overlay-active p,
        html.intake-overlay-active span,
        html.intake-overlay-active div,
        html.intake-overlay-active label {
          color: var(--succession-text-primary) !important;
        }
        /* Specific Tailwind classes use theme variables */
        html.intake-overlay-active .text-succession-text-primary { 
          color: var(--succession-text-primary) !important; 
        }
        html.intake-overlay-active .text-succession-text-secondary { 
          color: var(--succession-text-secondary) !important; 
        }
        html.intake-overlay-active .text-succession-foreground { 
          color: var(--succession-text-primary) !important; 
        }
        /* Legacy classes */
        html.intake-overlay-active body.wizard-body .text-muted { 
          color: var(--succession-text-secondary) !important; 
        }
        /* Buttons use theme-aware colors */
        html.intake-overlay-active .btn,
        html.intake-overlay-active .btn-default,
        html.intake-overlay-active .btn-link,
        html.intake-overlay-active .btn:disabled,
        html.intake-overlay-active .btn.disabled,
        html.intake-overlay-active button {
          color: var(--succession-text-primary) !important;
          border-color: var(--succession-border) !important;
        }
        /* Ensure buttons with specific backgrounds keep their text readable */
        html.intake-overlay-active .bg-succession-primary,
        html.intake-overlay-active .btn-primary {
          color: white !important;
        }
        /* Ensure proper background colors */
        html.intake-overlay-active .bg-succession-bg-primary {
          background-color: var(--succession-bg-primary) !important;
        }
        html.intake-overlay-active .bg-succession-surface {
          background-color: var(--succession-surface) !important;
        }
        `}
      </style>
      {step === 'processing' && <Processing />}
      {step === 'prompt_close_active' && (
        <Prompt
          title="Close Current Sprint?"
          body="You already have an active sprint. Proceeding will close it. You can move any tasks you want to keep into your Exit Plan later."
          onProceed={() => startCreate({ confirm: true, reuse: false })}
        />
      )}
      {step === 'prompt_reuse_existing' && (
        <Prompt
          title="Reuse Existing Exit Plan Sprint?"
          body="An Exit Plan Sprint already exists. Would you like to reuse and reactivate it now?"
          onProceed={() => startCreate({ confirm: false, reuse: true })}
        />
      )}
      {step === 'done' && <Done />}
    </div>
  );
  return ReactDOM.createPortal(overlayNode, portalElRef.current);
};

export default ExitPlanOverlay;


